import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { getRequiredFields } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema } from "./validations";
import {
  DOCUMENT_TYPE,
} from "./consts";
import type { ActiveTabType } from "./types";
import { useGetDocumentTypeDropdown, useGetGLConfig } from "@/services";
import { useIsMobile } from "@/hooks/use-mobile";

const useCreateJEManual = () => {
  const isMobile = useIsMobile();
  const { communityData, currentFinanceYear, loggedInUserData } = useSelector(
    (state: RootState) => state.currentData
  );
  const [activeTab, setActiveTab] = useState<ActiveTabType>("manual")
  const form = useForm<any>({
    defaultValues: {
      [DOCUMENT_TYPE]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const breadcrumbTitle = `Create JE Manual - ${
    communityData?.communityName || communityData?.name || ""
  }`;

  const breadcrumbData = [
    {
      link: `/${routes.admin}/${routes.activityDashboard}`,
      name: `Activity Dashboard - ${
        communityData?.communityName || communityData?.name || ""
      }`,
    },
    {
      link: "",
      name: "Create JE Manual",
    },
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const documentTypeId = useWatch({
    control: form.control,
    name: DOCUMENT_TYPE,
  });

  const {data: documentTypeList, isLoading: isListLoading} = useGetDocumentTypeDropdown({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  });

  const {data: configFields, isLoading: isRefetchingConfigFields} = useGetGLConfig({
    communityId: communityData?.communityId,
    documentTypeId: documentTypeId,
    queryParams: {
      enabled: !!(communityData?.communityId && documentTypeId)
    }
  });

  const headerFieldCount = useMemo(() => configFields?.data?.filter(
    (f: any) => f?.fieldLevel?.toLowerCase() == "header" && f.applicable
  ).length || 0, [configFields]);

  const lineItemFieldCount = useMemo(() => configFields?.data?.filter(
    (f: any) => f?.fieldLevel?.toLowerCase() == "line_item" && f.applicable
  )?.length || 0, [configFields]);

  const currentConfig = useMemo(
    () => documentTypeList?.find((data: any) => data?.documentTypeId == documentTypeId),
    [documentTypeId]
  );

  const isVp = useMemo(() => {
    return configFields?.data?.find((f: any) => f?.fieldLevel?.toLowerCase() == "header" && f.applicable)?.documentType == "VP";
  } ,[configFields]);

  const onTabValueChange = (value: string) => {
    setActiveTab(value as any);
  }

  return {
    isRefetchingConfigFields,
    isListLoading,
    documentTypeList,
    onTabValueChange,
    activeTab,
    breadcrumbTitle,
    breadcrumbData,
    currentFinanceYear,
    loggedInUserData,
    headerFieldCount,
    lineItemFieldCount,
    form,
    requiredFields,
    currentConfig,
    configFields: configFields?.data,
    isMobile,
    isVp
  };
};

export default useCreateJEManual;
